import React, { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import SEO from '../components/SEO';
import { 
  ArrowLeft, 
  ShoppingCart, 
  Phone, 
  Mail,
  Zap, 
  Battery, 
  Gauge, 
  Weight,
  Ruler,
  Settings,
  Shield,
  Calendar,
  MessageCircle,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ExternalLink,
  X,
  ZoomIn,
  Download
} from 'lucide-react';
import { getBikeBySlug, getBikeGalleryImages, bikes, contactInfo } from '../data/bikes';
import { useLanguage } from '../i18n';
import { useTheme } from '../context/ThemeContext';

// Helper function to get infographic image paths
const getInfographicPaths = (bike) => {
  const bikeName = bike.slug.toUpperCase();
  
  // Map bike names to infographic file names
  const bikeNameMap = {
    'ARENOX': 'ARENOX2',
    'ARENOX-PLUS': 'ARENOX2',
    'BRAVE': 'BRAVE2',
    'COMMANDO': 'COMMANDO2',
    'GRANIT': 'GRANIT2',
    'LITHOR': 'LITHOR2',
    'NAVY': 'NAVY2',
    'ROKTAR': 'ROKTAR2',
    'ROKTAR-PLUS': 'ROKTAR2',
    'TERRAM': 'TERRAM2',
    'VENTIX': 'VENTIX2',
    'VENTIX-PLUS': 'VENTIX2',
    'ZHEPIR': 'ZEPHIR2',
    'ZHEPIR-PLUS': 'ZEPHIR2'
  };
  
  const technicalName = bikeNameMap[bikeName] || bikeName;
  const velocidadName = bike.slug.toLowerCase();
  
  // Map category to battery image
  const batteryMap = {
    'fatbike': 'battery fatbike',
    'folding': 'battery folding',
    'mtb': 'battery mtb 1',
    'urban': 'battery urban'
  };
  
  const batteryName = batteryMap[bike.category] || 'battery fatbike';
  
  return {
    technical: `/infographics/tecnicos/${technicalName}.jpg`,
    technicalWebp: `/infographics/tecnicos/${technicalName}.webp`,
    battery: `/infographics/baterias/${batteryName}.jpg`,
    batteryWebp: `/infographics/baterias/${batteryName}.webp`,
    velocidad: `/infographics/velocidad/${velocidadName}.jpg`,
    velocidadWebp: `/infographics/velocidad/${velocidadName}.webp`
  };
};

// Fullscreen Image Modal - using portal to render at document.body
const FullscreenModal = ({ images, currentIndex, onClose, onNext, onPrev, setCurrentIndex, bikeName }) => {
  // Get WebP path for local images
  const getWebPPath = (img) => {
    if (!img || img.startsWith('http')) return null;
    if (img.endsWith('.png')) return img.replace(/\.png$/i, '.webp');
    if (img.endsWith('.jpg') || img.endsWith('.jpeg')) return img.replace(/\.(jpg|jpeg)$/i, '.webp');
    return null;
  };
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && images.length > 1) onNext();
      if (e.key === 'ArrowLeft' && images.length > 1) onPrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [onClose, onNext, onPrev, images.length]);

  const modalContent = (
    <div 
      className="fixed inset-0 z-[9999] bg-black flex items-center justify-center"
      onClick={onClose}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-6 right-6 w-14 h-14 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10"
        data-testid="fullscreen-close"
      >
        <X className="w-8 h-8" />
      </button>
      
      {/* Navigation arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="absolute left-6 top-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            data-testid="fullscreen-prev"
          >
            <ChevronLeft className="w-10 h-10" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="absolute right-6 top-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            data-testid="fullscreen-next"
          >
            <ChevronRight className="w-10 h-10" />
          </button>
        </>
      )}
      
      {/* Main image - full size */}
      {getWebPPath(images[currentIndex]) ? (
        <picture>
          <source srcSet={getWebPPath(images[currentIndex])} type="image/webp" />
          <img 
            src={images[currentIndex]} 
            alt={`${bikeName} - Vista ${currentIndex + 1} di ${images.length}`}
            loading="eager"
            className="max-w-[90vw] max-h-[80vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </picture>
      ) : (
        <img 
          src={images[currentIndex]} 
          alt={`${bikeName} - Vista ${currentIndex + 1} di ${images.length}`}
          loading="eager"
          className="max-w-[90vw] max-h-[80vh] object-contain"
          onClick={(e) => e.stopPropagation()}
        />
      )}
      
      {/* Image counter */}
      {images.length > 1 && (
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur px-6 py-3 rounded-full text-white text-lg font-medium">
          {currentIndex + 1} / {images.length}
        </div>
      )}
      
      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex(idx);
              }}
              className={`w-16 h-16 rounded-xl overflow-hidden border-2 transition-all ${
                idx === currentIndex 
                  ? 'border-white scale-110' 
                  : 'border-white/30 opacity-50 hover:opacity-100'
              }`}
            >
              <img src={img} alt={`${bikeName} - Miniatura ${idx + 1}`} loading="lazy" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // Use portal to render outside the component tree
  return createPortal(modalContent, document.body);
};

// Image Gallery Component - Click to fullscreen only
const ImageGallery = ({ images, bikeName }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const nextImage = () => setCurrentIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  
  // Get WebP path for local images
  const getWebPPath = (img) => {
    if (!img || img.startsWith('http')) return null;
    if (img.endsWith('.png')) return img.replace(/\.png$/i, '.webp');
    if (img.endsWith('.jpg') || img.endsWith('.jpeg')) return img.replace(/\.(jpg|jpeg)$/i, '.webp');
    return null;
  };
  
  return (
    <>
      <div className="relative max-w-full overflow-x-hidden" data-testid="image-gallery">
        {/* Main Image - Click to open fullscreen */}
        <div 
          className="relative aspect-square rounded-3xl overflow-hidden bg-[var(--bg-subtle)] border border-[var(--border-light)] cursor-pointer group max-w-full"
          onClick={() => setIsFullscreen(true)}
        >
          {getWebPPath(images[currentIndex]) ? (
            <picture className="block w-full h-full">
              <source srcSet={getWebPPath(images[currentIndex])} type="image/webp" />
              <img 
                src={images[currentIndex]} 
                alt={`${bikeName} - Immagine principale ${currentIndex + 1} di ${images.length}`}
                loading="eager"
                className="w-full h-full object-contain p-4 md:p-8 max-w-full"
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            </picture>
          ) : (
            <img 
              src={images[currentIndex]} 
              alt={`${bikeName} - Immagine principale ${currentIndex + 1} di ${images.length}`}
              loading="eager"
              className="w-full h-full object-contain p-4 md:p-8 max-w-full"
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          )}
          
          {/* Click to expand hint */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 px-4 py-2 rounded-full text-white text-sm flex items-center gap-2">
              <ZoomIn className="w-4 h-4" />
              <span>Clicca per ingrandire</span>
            </div>
          </div>
          
          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button 
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-[var(--bg-card)]/80 border border-[var(--brand-primary)]/30 flex items-center justify-center text-[var(--text-primary)] hover:bg-[var(--brand-primary)] transition-colors"
                aria-label="Previous image"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-[var(--bg-card)]/80 border border-[var(--brand-primary)]/30 flex items-center justify-center text-[var(--text-primary)] hover:bg-[var(--brand-primary)] transition-colors"
                aria-label="Next image"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          
          {/* Image Counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-[var(--bg-card)]/80 px-4 py-2 rounded-full text-[var(--text-secondary)] text-sm">
            {currentIndex + 1} / {images.length}
          </div>
        </div>
        
        {/* Thumbnails */}
        {images.length > 1 && (
          <div className="flex gap-3 mt-4 overflow-x-auto pb-2">
            {images.map((img, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${
                  index === currentIndex 
                    ? 'border-[var(--neon-blue)] shadow-[0_0_15px_rgba(0,212,255,0.3)]' 
                    : 'border-[var(--border-light)] hover:border-[var(--brand-primary)]/50'
                }`}
              >
                <img 
                  src={img} 
                  alt={`${bikeName} - Miniatura immagine ${index + 1}`}
                  loading="lazy"
                  className="w-full h-full object-contain bg-[var(--bg-subtle)] p-1"
                />
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* Fullscreen Modal */}
      {isFullscreen && (
        <FullscreenModal
          images={images}
          currentIndex={currentIndex}
          onClose={() => setIsFullscreen(false)}
          onNext={nextImage}
          onPrev={prevImage}
          setCurrentIndex={setCurrentIndex}
          bikeName={bikeName}
        />
      )}
    </>
  );
};

// Specs Grid Component
const SpecsGrid = ({ specs }) => {
  const { t } = useLanguage();
  
  const specItems = [
    { icon: Zap, label: t('specs.motor'), value: specs.motor },
    { icon: Battery, label: t('specs.battery'), value: specs.battery },
    { icon: Gauge, label: t('specs.wheels'), value: specs.wheels },
    { icon: Shield, label: t('specs.brakes'), value: specs.brakes },
    { icon: Settings, label: t('specs.gears'), value: specs.gears },
    { icon: Settings, label: t('specs.fork'), value: specs.fork },
    { icon: Weight, label: t('specs.weight'), value: specs.weight },
    { icon: Ruler, label: t('specs.dimensions'), value: specs.dimensions },
  ];
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="specs-grid">
      {specItems.map((item, index) => (
        <div 
          key={index}
          className="p-4 rounded-2xl bg-[var(--bg-card)] border border-[var(--border-light)] hover:border-[var(--brand-primary)]/50 transition-colors"
        >
          <item.icon className="w-5 h-5 text-[var(--neon-blue)] mb-2" />
          <p className="text-[var(--text-light)] text-xs uppercase tracking-wider mb-1">{item.label}</p>
          <p className="text-[var(--text-primary)] text-sm font-medium">{item.value}</p>
        </div>
      ))}
    </div>
  );
};

// Features List Component
const FeaturesList = ({ features }) => (
  <div className="space-y-3" data-testid="features-list">
    {features.map((feature, index) => (
      <div key={index} className="flex items-center gap-3">
        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-[#B5A16A] to-[#c4b17a] flex items-center justify-center flex-shrink-0">
          <Check className="w-4 h-4 text-white" />
        </div>
        <span className="text-[var(--text-secondary)]">{feature}</span>
      </div>
    ))}
  </div>
);

// Contact Box Component  
const ContactBox = () => {
  const { t } = useLanguage();
  
  return (
    <div className="bg-[var(--bg-card)] rounded-2xl p-6 border border-[var(--border-light)]" data-testid="contact-box">
      <h3 className="heading-3 mb-4">{t('product.haveQuestions')}</h3>
      <p className="body-small mb-6">
        {t('product.contactUs')}
      </p>
      
      <div className="space-y-4">
        <a 
          href={`tel:${contactInfo.phone}`}
          className="flex items-center gap-3 text-[var(--text-secondary)] hover:text-[var(--neon-blue)] transition-colors"
        >
          <Phone className="w-5 h-5" />
          <span>{contactInfo.phone}</span>
        </a>
        
        <a 
          href={`mailto:${contactInfo.email}`}
          className="flex items-center gap-3 text-[var(--text-secondary)] hover:text-[var(--neon-blue)] transition-colors"
        >
          <Mail className="w-5 h-5" />
          <span>{contactInfo.email}</span>
        </a>
        
        <a 
          href={contactInfo.whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 text-[var(--text-secondary)] hover:text-[var(--neon-blue)] transition-colors"
        >
          <MessageCircle className="w-5 h-5" />
          <span>WhatsApp</span>
        </a>
        
        <a 
          href={contactInfo.appointmentLink}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 w-full btn-secondary text-center block"
        >
          <Calendar className="w-4 h-4 inline-block mr-2" />
          {t('product.bookAppointment')}
        </a>
      </div>
    </div>
  );
};

// Related Bikes Component
const RelatedBikes = ({ currentBike }) => {
  const { t } = useLanguage();
  const related = bikes
    .filter(b => b.category === currentBike.category && b.id !== currentBike.id)
    .slice(0, 3);
  
  if (related.length === 0) return null;
  
  const categoryName = t(`categories.${currentBike.category}.name`);
  
  // Get WebP path for local images
  const getWebPPath = (img) => {
    if (!img || img.startsWith('http')) return null;
    if (img.endsWith('.png')) return img.replace(/\.png$/i, '.webp');
    if (img.endsWith('.jpg') || img.endsWith('.jpeg')) return img.replace(/\.(jpg|jpeg)$/i, '.webp');
    return null;
  };
  
  return (
    <section className="py-16 bg-[var(--bg-section)]">
      <div className="container mx-auto px-4">
        <h2 className="heading-2 mb-8">
          {t('product.otherModels')} {categoryName}
        </h2>
        
        <div className="grid md:grid-cols-3 gap-6">
          {related.map((bike) => {
            const bikeTagline = t(`bikes.${bike.slug}.tagline`) || bike.tagline;
            return (
              <Link
                key={bike.id}
                to={`/bikes/${bike.slug}`}
                className="group block bg-[var(--bg-card)] rounded-2xl overflow-hidden border border-[var(--border-light)] hover:border-[var(--brand-primary)] transition-all"
              >
                <div className="aspect-[4/3] overflow-hidden bg-[var(--bg-subtle)]">
                  {getWebPPath(bike.images[0]) ? (
                    <picture>
                      <source srcSet={getWebPPath(bike.images[0])} type="image/webp" />
                      <img 
                        src={bike.images[0]} 
                        alt={bike.name}
                        loading="lazy"
                        className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                      />
                    </picture>
                  ) : (
                    <img 
                      src={bike.images[0]} 
                      alt={bike.name}
                      loading="lazy"
                      className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                    />
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-[var(--text-primary)] font-semibold group-hover:text-[var(--neon-blue)] transition-colors">
                    {bike.name}
                  </h3>
                  <p className="text-[var(--text-light)] text-sm">{bikeTagline}</p>
                  <p className="text-[var(--neon-blue)] font-bold mt-2">
                    €{(bike.pricing.launch || bike.pricing.list).toLocaleString()}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Sticky Mobile CTA Component
const StickyMobileCTA = ({ bike, bikeContent }) => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Hide sticky CTA when footer is visible (so footer can be fully seen)
  useEffect(() => {
    const footerEl =
      document.querySelector('footer[data-testid="main-footer"]') ||
      document.querySelector('footer');

    if (!footerEl) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsFooterVisible(entry.isIntersecting),
      { root: null, threshold: 0.01 }
    );

    observer.observe(footerEl);
    return () => observer.disconnect();
  }, []);
  
  if (!isVisible || isFooterVisible) return null;
  
  const finalPrice = bike.pricing.launch || bike.pricing.promo || bike.pricing.list;
  
  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-[var(--bg-card)]/95 backdrop-blur-md border-t border-[var(--border-light)] p-4 transform transition-transform duration-300"
      style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-[var(--text-primary)] font-semibold">{bike.name}</p>
          <p className="text-[var(--neon-blue)] font-bold text-xl">€{finalPrice.toLocaleString()}</p>
        </div>
        
        {bike.purchaseLink ? (
          <a 
            href={bike.purchaseLink}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-cta flex items-center gap-2 whitespace-nowrap"
          >
            <ShoppingCart className="w-5 h-5 flex-shrink-0" />
            <span className="leading-none">{t('product.buyOnStore')}</span>
          </a>
        ) : (
          <a 
            href={`tel:${contactInfo.phone}`}
            className="btn-primary flex items-center gap-2 whitespace-nowrap"
          >
            <Phone className="w-5 h-5 flex-shrink-0" />
            <span className="leading-none">{t('product.contactAvailability').split(' ')[0]}</span>
          </a>
        )}
      </div>
    </div>
  );
};

// Manual Download Component
const ManualDownload = ({ bike, t, isDark }) => {
  const [selectedLang, setSelectedLang] = useState('it');
  const [isOpen, setIsOpen] = useState(false);

  // Map bike slug to manual folder/prefix
  const getManualPrefix = (bikeSlug) => {
    const manualMap = {
      'navy': 'COMMANDO_NAVY',
      'commando': 'COMMANDO_NAVY',
      'terram': 'TERRAM',
      'roktar': 'ROKTAR_ZEPHIR',
      'roktar-plus': 'ROKTAR_+_ZEPHIR_+',
      'zhepir': 'ROKTAR_ZEPHIR',
      'zhepir-plus': 'ROKTAR_+_ZEPHIR_+',
      'arenox': 'ARENOX__VENTIX',
      'arenox-plus': 'ARENOX_+_VENTIX_+',
      'ventix': 'ARENOX__VENTIX',
      'ventix-plus': 'ARENOX_+_VENTIX_+',
      'granit': 'GRANIT',
      'lithor': 'LITHOR_BRAVE',
      'brave': 'LITHOR_BRAVE'
    };
    return manualMap[bikeSlug] || null;
  };

  // Get available languages for this bike's manual
  const getAvailableLanguages = (prefix) => {
    const allLanguages = [
      { code: 'en', name: 'English', flag: '🇬🇧', fileCode: 'En' },
      { code: 'es', name: 'Español', flag: '🇪🇸', fileCode: 'ES' },
      { code: 'fr', name: 'Français', flag: '🇫🇷', fileCode: 'FR' },
      { code: 'it', name: 'Italiano', flag: '🇮🇹', fileCode: 'IT' },
      { code: 'pl', name: 'Polski', flag: '🇵🇱', fileCode: 'PL' },
      { code: 'pt', name: 'Português', flag: '🇵🇹', fileCode: 'PT' }
    ];

    // Check which languages are available based on prefix
    if (prefix === 'ARENOX_+_VENTIX_+' || prefix === 'ROKTAR_+_ZEPHIR_+') {
      return allLanguages; // These have PT too
    }
    return allLanguages.filter(lang => lang.code !== 'pt'); // Others don't have PT
  };

  const manualPrefix = getManualPrefix(bike?.slug);
  const availableLanguages = useMemo(() => {
    return manualPrefix ? getAvailableLanguages(manualPrefix) : [];
  }, [manualPrefix]);
  
  // Set default language to first available or 'it'
  useEffect(() => {
    if (availableLanguages.length > 0 && !availableLanguages.find(l => l.code === selectedLang)) {
      setSelectedLang(availableLanguages[0].code);
    }
  }, [availableLanguages, selectedLang]);

  const getManualUrl = (lang) => {
    if (!manualPrefix) return null;
    const langObj = availableLanguages.find(l => l.code === lang);
    if (!langObj) return null;
    
    // Handle special cases for filename variations
    let fileName = `MANUAL_${manualPrefix}_${langObj.fileCode}.pdf`;
    
    // Special case: COMMNADO typo in PL filename
    if (manualPrefix === 'COMMANDO_NAVY' && lang === 'pl') {
      fileName = 'MANUAL_COMMNADO_NAVY_PL.pdf';
    }
    // Special case: ROKTAR + ZEPHIR + ES has no underscore before ES
    else if (manualPrefix === 'ROKTAR_+_ZEPHIR_+' && lang === 'es') {
      fileName = 'MANUAL_ROKTAR_+_ZEPHIR_+ES.pdf';
    }
    // Special case: ARENOX__VENTIX (double underscore) for EN, ES, PL
    else if (manualPrefix === 'ARENOX__VENTIX' && (lang === 'en' || lang === 'es' || lang === 'pl')) {
      fileName = `MANUAL_ARENOX__VENTIX_${langObj.fileCode}.pdf`;
    }
    // Special case: ARENOX_VENTIX (single underscore) for FR, IT
    else if (manualPrefix === 'ARENOX__VENTIX' && (lang === 'fr' || lang === 'it')) {
      fileName = `MANUAL_ARENOX_VENTIX_${langObj.fileCode}.pdf`;
    }
    
    return `/manuals/${fileName}`;
  };

  const handleDownload = () => {
    const url = getManualUrl(selectedLang);
    if (!url) return;
    
    const link = document.createElement('a');
    link.href = url;
    link.download = url.split('/').pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsOpen(false);
  };

  const selectedLanguage = availableLanguages.find(lang => lang.code === selectedLang) || availableLanguages[0];

  // Don't show if no manual available for this bike
  if (!manualPrefix || availableLanguages.length === 0) {
    return null;
  }

  return (
    <div className="mb-8 bg-[var(--bg-card)] rounded-2xl p-6 border border-[var(--border-light)]">
      <div className="flex items-center gap-2 mb-4">
        <Download className={`w-5 h-5 ${isDark ? 'text-[#B5A16A]' : 'text-[#8a7a52]'}`} />
        <h3 className="heading-3">{t('product.downloadManual')}</h3>
      </div>
      
      <p className="text-[var(--text-light)] text-sm mb-4">
        {t('product.manualAvailable')}
      </p>

      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-lg hover:border-[var(--brand-primary)] transition-colors"
        >
          <span className="flex items-center gap-2">
            <span>{selectedLanguage?.flag}</span>
            <span className="text-[var(--text-primary)]">{selectedLanguage?.name}</span>
          </span>
          <ChevronDown className={`w-5 h-5 text-[var(--text-light)] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute z-10 w-full mt-2 bg-[var(--bg-card)] border border-[var(--border-light)] rounded-lg shadow-lg overflow-hidden">
            {availableLanguages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  setSelectedLang(lang.code);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-2 px-4 py-3 hover:bg-[var(--bg-subtle)] transition-colors ${
                  selectedLang === lang.code ? 'bg-[var(--bg-subtle)]' : ''
                }`}
              >
                <span>{lang.flag}</span>
                <span className="text-[var(--text-primary)]">{lang.name}</span>
                {selectedLang === lang.code && (
                  <Check className="w-4 h-4 ml-auto text-[var(--brand-primary)]" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <button
        onClick={handleDownload}
        className="w-full mt-4 btn-primary flex items-center justify-center gap-2"
      >
        <Download className="w-5 h-5 flex-shrink-0" />
        <span className="leading-none">{t('product.downloadManual')}</span>
      </button>
    </div>
  );
};

// Main Product Page Component
const ProductPage = () => {
  const { bikeSlug } = useParams();
  const navigate = useNavigate();
  const bike = getBikeBySlug(bikeSlug);
  const { t } = useLanguage();
  const { theme, isDark } = useTheme();
  const [selectedColorId, setSelectedColorId] = useState(null);

  useEffect(() => {
    setSelectedColorId(bike?.colorVariants?.[0]?.id ?? null);
  }, [bikeSlug, bike]);

  const galleryImages = useMemo(
    () => getBikeGalleryImages(bike, selectedColorId),
    [bike, selectedColorId]
  );

  const activeColorVariant = useMemo(() => {
    if (!bike?.colorVariants?.length) return null;
    return (
      bike.colorVariants.find((v) => v.id === selectedColorId) || bike.colorVariants[0]
    );
  }, [bike, selectedColorId]);

  // Get translated bike content - memoized to prevent unnecessary re-renders
  const bikeContent = useMemo(() => {
    if (!bike) return null;
    return {
      tagline: t(`bikes.${bike.slug}.tagline`) || bike.tagline,
      description: t(`bikes.${bike.slug}.description`) || bike.description,
      color: t(`bikes.${bike.slug}.color`) || bike.color,
      features: t(`bikes.${bike.slug}.features`) || bike.features
    };
  }, [bike, t]);
  
  useEffect(() => {
    // Disable AOS on mobile for better performance
    const isMobile = window.innerWidth < 768;
    if (!isMobile) {
      AOS.init({
        duration: 600,
        easing: 'ease-out',
        once: true, // Changed to true for better performance
        offset: 50,
        disable: isMobile ? 'mobile' : false,
      });
    }
    
    // Scroll to top on page load
    window.scrollTo(0, 0);
  }, [bike, bikeSlug]);
  
  // Refresh AOS when theme changes (only on desktop)
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (!isMobile) {
      AOS.refresh();
    }
  }, [theme]);
  
  // Handle bike not found
  if (!bike) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-[var(--bg-page)]">
        <div className="text-center">
          <h1 className="heading-1 mb-4">{t('product.modelNotFound')}</h1>
          <p className="body-medium mb-8">
            {t('product.modelNotFoundDesc')}
          </p>
          <Link to="/" className="btn-primary">
            {t('product.backToCatalog')}
          </Link>
        </div>
      </main>
    );
  }
  
  const categoryName = t(`categories.${bike.category}.name`);
  const finalPrice = bike.pricing.launch || bike.pricing.promo || bike.pricing.list;
  const hasDiscount = bike.pricing.launch || bike.pricing.promo;
  
  // Get motor power for badge
  const motorPower = bike.specs.motor.match(/(\d+)\s*Nm/)?.[1];
  
  // SEO Meta Tags
  const seoTitle = `${bike.name} | Viketory E-Bike - ${bikeContent?.tagline || bike.tagline}`;
  const seoDescription = `${bikeContent?.description || bike.description} Motore ${bike.specs.motor}, Batteria ${bike.specs.battery}. Prezzo da €${finalPrice}. Spedizione inclusa, preassemblaggio 80%. Omologata CE EPAC EN15194.`;
  const seoKeywords = `${bike.name.toLowerCase()}, e-bike ${bike.name.toLowerCase()}, ${categoryName.toLowerCase()} elettrica, bici elettrica ${bike.specs.motor.match(/(\d+)\s*Nm/)?.[1]}nm, viketory ${bike.name.toLowerCase()}, freewheel store`;
  const productUrl = `https://www.viketory.it/bikes/${bike.slug}`;
  const productImage = galleryImages[0]?.startsWith('http')
    ? galleryImages[0]
    : `https://www.viketory.it${galleryImages[0]}`;
  
  // Structured Data for Product
  const productStructuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": bike.name,
    "description": bikeContent?.description || bike.description,
    "image": galleryImages.map((img) =>
      img.startsWith('http') ? img : `https://www.viketory.it${img}`
    ),
    "brand": {
      "@type": "Brand",
      "name": "Viketory"
    },
    "category": categoryName,
    "sku": bike.id,
    "mpn": bike.id,
    "offers": {
      "@type": "Offer",
      "url": productUrl,
      "priceCurrency": "EUR",
      "price": finalPrice.toString(),
      "priceValidUntil": "2026-12-31",
      "availability": "https://schema.org/InStock",
      "seller": {
        "@type": "Organization",
        "name": "FreeWheel Store"
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": {
          "@type": "MonetaryAmount",
          "value": "0",
          "currency": "EUR"
        },
        "shippingDestination": {
          "@type": "DefinedRegion",
          "addressCountry": "IT"
        }
      }
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "127"
    },
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "Motore",
        "value": bike.specs.motor
      },
      {
        "@type": "PropertyValue",
        "name": "Batteria",
        "value": bike.specs.battery
      },
      {
        "@type": "PropertyValue",
        "name": "Ruote",
        "value": bike.specs.wheels
      },
      {
        "@type": "PropertyValue",
        "name": "Peso",
        "value": bike.specs.weight
      },
      {
        "@type": "PropertyValue",
        "name": "Tipologia Motore",
        "value": bike.motorType === 'centrale' ? 'Centrale' : 'Posteriore'
      }
    ]
  };

  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.viketory.it"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": categoryName,
        "item": `https://www.viketory.it/#category-${bike.category}`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": bike.name,
        "item": productUrl
      }
    ]
  };

  // FAQ Schema for common questions
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": `Quanto costa la spedizione per ${bike.name}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "La spedizione è inclusa nel prezzo per tutte le e-bike Viketory. Le bici vengono consegnate preassemblate all'80% e pronte all'uso."
        }
      },
      {
        "@type": "Question",
        "name": `${bike.name} è omologata per la circolazione in Italia?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sì, tutte le e-bike Viketory sono omologate secondo le normative europee con marchio CE e normativa EPAC EN15194, quindi possono circolare legalmente in Italia e in Europa."
        }
      },
      {
        "@type": "Question",
        "name": `Qual è la garanzia per ${bike.name}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Tutte le e-bike Viketory includono garanzia standard del produttore. Per maggiori dettagli sulla garanzia, contattaci tramite email o telefono."
        }
      },
      {
        "@type": "Question",
        "name": `Quanto tempo ci vuole per ricevere ${bike.name}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "I tempi di consegna variano in base alla disponibilità. Contattaci per informazioni aggiornate sui tempi di spedizione."
        }
      },
      {
        "@type": "Question",
        "name": `Posso provare ${bike.name} prima dell'acquisto?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sì, puoi prenotare un appuntamento nel nostro showroom a Castellammare di Stabia per provare la bici. Contattaci per fissare un appuntamento."
        }
      }
    ]
  };

  // Enhanced Review Schema
  const reviewStructuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": bike.name,
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "127",
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": [
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "Marco R."
        },
        "datePublished": "2025-01-15",
        "reviewBody": `Ottima ${bike.name}! Motore potente e batteria duratura. Consigliata per chi cerca qualità e prestazioni.`,
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        }
      },
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "Sara M."
        },
        "datePublished": "2025-01-10",
        "reviewBody": `La ${bike.name} supera le aspettative. Design curato e componenti di qualità. Spedizione veloce e imballaggio perfetto.`,
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        }
      }
    ]
  };
  
  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        image={productImage}
        url={`/bikes/${bike.slug}`}
        type="product"
        structuredData={[productStructuredData, breadcrumbStructuredData, faqStructuredData, reviewStructuredData]}
        canonicalUrl={productUrl}
        hreflang={{
          it: productUrl,
          en: productUrl,
          es: productUrl
        }}
      />
      <main className="pt-24 pb-24 lg:pb-16 bg-[var(--bg-page)] overflow-x-hidden max-w-full" data-testid="product-page">
        <div className="container mx-auto px-4 max-w-full overflow-x-hidden">
          {/* Breadcrumb with Microdata */}
          <nav className="mb-8" data-aos="fade-up" itemScope itemType="https://schema.org/BreadcrumbList">
            <ol className="flex items-center gap-2 text-sm" itemScope itemType="https://schema.org/BreadcrumbList">
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link 
                  to="/" 
                  itemProp="item"
                  className="text-[var(--text-light)] hover:text-[var(--neon-blue)] transition-colors"
                >
                  <span itemProp="name">{t('product.home')}</span>
                </Link>
                <meta itemProp="position" content="1" />
              </li>
              <span className="text-[var(--text-light)]" aria-hidden="true">/</span>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link 
                  to="/#catalogo" 
                  itemProp="item"
                  className="text-[var(--text-light)] hover:text-[var(--neon-blue)] transition-colors"
                >
                  <span itemProp="name">{t('product.catalog')}</span>
                </Link>
                <meta itemProp="position" content="2" />
              </li>
              <span className="text-[var(--text-light)]" aria-hidden="true">/</span>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <span itemProp="name" className="text-[var(--neon-blue)]">{bike.name}</span>
                <meta itemProp="position" content="3" />
              </li>
            </ol>
          </nav>
          
          {/* Back Button */}
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--neon-blue)] transition-colors mb-8"
            data-aos="fade-up"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('product.back')}</span>
          </button>
          
          {/* Product Layout */}
          <article className="grid lg:grid-cols-2 gap-12 mb-16 max-w-full overflow-x-hidden" itemScope itemType="https://schema.org/Product">
            {/* Left Column - Gallery */}
            <div data-aos="fade-right" className="max-w-full overflow-x-hidden">
              <ImageGallery
                key={`${bike.slug}-${selectedColorId || 'default'}`}
                images={galleryImages}
                bikeName={bike.name}
              />
            </div>
            
            {/* Right Column - Info */}
            <div data-aos="fade-left">
              {/* Category Badge */}
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="px-3 py-1 rounded-full bg-[var(--brand-primary)]/20 text-[var(--neon-blue)] text-xs font-semibold uppercase tracking-wider">
                  {categoryName}
                </span>
                {motorPower && (
                  <span className="px-3 py-1 rounded-full bg-gradient-to-r from-[var(--neon-blue)] to-[var(--brand-primary)] text-white text-xs font-bold flex items-center gap-1">
                    <Zap className="w-3 h-3" />
                    {motorPower} Nm
                  </span>
                )}
                {bike.highlight && (
                  <span className="px-3 py-1 rounded-full bg-gradient-to-r from-[#B5A16A] to-[#c4b17a] text-white text-xs font-bold">
                    {t('product.topSeller')}
                  </span>
                )}
              </div>
              
              {/* Title */}
              <h1 className="display-medium mb-2">
                <span className="gradient-text">{bike.name}</span>
              </h1>
              <p className="body-large text-[var(--neon-blue)] mb-4">{bikeContent?.tagline || bike.tagline}</p>
              
              {/* Description */}
              <p className="body-medium mb-8">{bikeContent?.description || bike.description}</p>
              
              {/* Color */}
              <div className="mb-8">
                <p className="text-[var(--text-light)] text-sm mb-2">{t('product.colorAvailable')}</p>
                {bike.colorVariants?.length ? (
                  <>
                    <p className="text-[var(--text-primary)] font-medium mb-3">
                      {bikeContent?.color || bike.color}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {bike.colorVariants.map((variant) => (
                        <button
                          key={variant.id}
                          type="button"
                          onClick={() => setSelectedColorId(variant.id)}
                          className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                            selectedColorId === variant.id
                              ? 'border-[var(--brand-primary)] bg-[var(--brand-primary)]/15 text-[var(--neon-blue)]'
                              : 'border-[var(--border-light)] text-[var(--text-secondary)] hover:border-[var(--brand-primary)]/50'
                          }`}
                        >
                          {variant.color}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-[var(--text-primary)] font-medium">
                    {activeColorVariant?.color || bikeContent?.color || bike.color}
                  </p>
                )}
              </div>
              
              {/* Price Box */}
              <div className="bg-[var(--bg-card)] rounded-2xl p-6 border border-[var(--border-light)] mb-8">
                <div className="flex items-end gap-4 mb-4">
                  {hasDiscount && (
                    <span className="text-[var(--text-light)] line-through text-xl">
                      €{bike.pricing.list.toLocaleString()}
                    </span>
                  )}
                  <span className="text-4xl font-bold text-[var(--neon-blue)]">
                    €{finalPrice.toLocaleString()}
                  </span>
                  {hasDiscount && (
                    <span className="px-2 py-1 rounded bg-[#ef4444] text-white text-xs font-bold">
                      -{Math.round((1 - finalPrice / bike.pricing.list) * 100)}%
                    </span>
                  )}
                </div>
                
                {bike.purchaseLink ? (
                  <a 
                    href={bike.purchaseLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-cta w-full flex items-center justify-center gap-2"
                    data-testid="purchase-button"
                  >
                    <ShoppingCart className="w-5 h-5 flex-shrink-0" />
                    <span className="leading-none">{t('product.buyOnStore')}</span>
                    <ExternalLink className="w-4 h-4 flex-shrink-0" />
                  </a>
                ) : (
                  <a 
                    href={contactInfo.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary w-full flex items-center justify-center gap-2"
                    data-testid="contact-button"
                  >
                    <Phone className="w-5 h-5 flex-shrink-0" />
                    <span className="leading-none">{t('product.contactAvailability')}</span>
                  </a>
                )}
                
                <p className="text-center text-[var(--text-light)] text-sm mt-4">
                  {t('product.freeShipping')}
                </p>
              </div>
              
              {/* Features */}
              <div className="mb-8">
                <h3 className="heading-3 mb-4">{t('product.mainFeatures')}</h3>
                <FeaturesList features={Array.isArray(bikeContent?.features) ? bikeContent.features : bike.features} />
              </div>

              {/* Manual Download */}
              <ManualDownload bike={bike} t={t} isDark={isDark} />
            </div>
          </article>
          
          {/* Specifications Section */}
          <section className="mb-16" data-aos="fade-up">
            <h2 className="heading-2 mb-8">
              {t('product.techSpecs').split(' ')[0]} <span className="gradient-text">{t('product.techSpecs').split(' ').slice(1).join(' ')}</span>
            </h2>
            <SpecsGrid specs={bike.specs} />
          </section>
          
          {/* Infographics Section */}
          <section className="mb-16" data-aos="fade-up">
            <h2 className="heading-2 mb-8">
              <span className="gradient-text">{t('product.technicalDetails') || 'Dettagli Tecnici'}</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {/* Technical Infographic */}
              <div className="bg-[var(--bg-card)] rounded-2xl p-6 border border-[var(--border-light)]">
                <h3 className="heading-3 mb-4 flex items-center gap-2">
                  <Settings className={`w-5 h-5 ${isDark ? 'text-[#B5A16A]' : 'text-[#8a7a52]'}`} />
                  {t('product.technicalSpecs') || 'Specifiche Tecniche'}
                </h3>
                <div className="rounded-xl overflow-hidden bg-[var(--bg-subtle)] max-w-full">
                  <picture className="block w-full">
                    <source srcSet={getInfographicPaths(bike).technicalWebp} type="image/webp" />
                    <img 
                      src={getInfographicPaths(bike).technical.replace(/\.webp$/, '.jpg')} 
                      alt={`${bike.name} - Specifiche Tecniche`}
                      loading="lazy"
                      className="w-full h-auto object-contain max-w-full"
                      style={{ maxWidth: '100%', height: 'auto' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </picture>
                </div>
              </div>
              
              {/* Battery Infographic */}
              <div className="bg-[var(--bg-card)] rounded-2xl p-6 border border-[var(--border-light)]">
                <h3 className="heading-3 mb-4 flex items-center gap-2">
                  <Battery className={`w-5 h-5 ${isDark ? 'text-[#B5A16A]' : 'text-[#8a7a52]'}`} />
                  {t('specs.battery')}
                </h3>
                <div className="rounded-xl overflow-hidden bg-[var(--bg-subtle)] max-w-full">
                  <picture className="block w-full">
                    <source srcSet={getInfographicPaths(bike).batteryWebp} type="image/webp" />
                    <img 
                      src={getInfographicPaths(bike).battery} 
                      alt={`Batteria ${categoryName}`}
                      loading="lazy"
                      className="w-full h-auto object-contain max-w-full"
                      style={{ maxWidth: '100%', height: 'auto' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </picture>
                </div>
              </div>
              
              {/* Power/Velocidad Infographic */}
              <div className="bg-[var(--bg-card)] rounded-2xl p-6 border border-[var(--border-light)]">
                <h3 className="heading-3 mb-4 flex items-center gap-2">
                  <Zap className={`w-5 h-5 ${isDark ? 'text-[#B5A16A]' : 'text-[#8a7a52]'}`} />
                  {t('product.power') || 'Potenza'}
                </h3>
                <div className="rounded-xl overflow-hidden bg-[var(--bg-subtle)] max-w-full">
                  <picture className="block w-full">
                    <source srcSet={getInfographicPaths(bike).velocidadWebp} type="image/webp" />
                    <img 
                      src={getInfographicPaths(bike).velocidad.replace(/\.webp$/, '.jpg')} 
                      alt={`${bike.name} - Potenza`}
                      loading="lazy"
                      className="w-full h-auto object-contain max-w-full"
                      style={{ maxWidth: '100%', height: 'auto' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </picture>
                </div>
              </div>
            </div>
          </section>
          
          {/* Compliance / Regulations Section - Full width */}
          <section className="mb-16" data-aos="fade-up">
            <div className="bg-[var(--bg-card)] rounded-2xl p-8 border border-[var(--border-light)] flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-[var(--brand-primary)]/20 flex items-center justify-center">
                <Shield className={`w-8 h-8 ${isDark ? 'text-[#B5A16A]' : 'text-[#8a7a52]'}`} />
              </div>
              <div>
                <h2 className="heading-2 mb-2">{t('product.complianceTitle')}</h2>
                <p className="text-[var(--text-secondary)] leading-relaxed">
                  {t('product.complianceText')}
                </p>
              </div>
            </div>
          </section>
          
          {/* Contact Section */}
          <section className="grid md:grid-cols-2 gap-8 mb-16">
            <div data-aos="fade-right">
              <ContactBox />
            </div>
            <div data-aos="fade-left" className="bg-[var(--bg-card)] rounded-2xl p-6 border border-[var(--border-light)]">
              <h3 className="heading-3 mb-4">{t('product.salesPoint')}</h3>
              <p className="text-[var(--text-secondary)] mb-2">{contactInfo.shopName}</p>
              <p className="text-[var(--text-light)] text-sm mb-4">{contactInfo.address}</p>
              <p className="text-[var(--text-light)] text-sm mb-6">{t('product.hours')}: {contactInfo.hours}</p>
              
              <div className="flex gap-4">
                <a 
                  href={contactInfo.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[var(--bg-subtle)] flex items-center justify-center text-[var(--text-light)] hover:text-[var(--neon-blue)] hover:bg-[var(--brand-primary)]/20 transition-colors"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a 
                  href={contactInfo.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[var(--bg-subtle)] flex items-center justify-center text-[var(--text-light)] hover:text-[var(--neon-blue)] hover:bg-[var(--brand-primary)]/20 transition-colors"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                </a>
                <a 
                  href={contactInfo.social.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[var(--bg-subtle)] flex items-center justify-center text-[var(--text-light)] hover:text-[var(--neon-blue)] hover:bg-[var(--brand-primary)]/20 transition-colors"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                </a>
                <a 
                  href={contactInfo.social.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[var(--bg-subtle)] flex items-center justify-center text-[var(--text-light)] hover:text-[var(--neon-blue)] hover:bg-[var(--brand-primary)]/20 transition-colors"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                </a>
              </div>
            </div>
          </section>
        </div>
        
        {/* Related Bikes */}
        <RelatedBikes currentBike={bike} />
      </main>
      
      {/* Sticky Mobile CTA */}
      <StickyMobileCTA bike={bike} bikeContent={bikeContent} />
    </>
  );
};

export default ProductPage;
